import type { Task, WorkerRecord } from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useSession } from '@/components/auth/session';
import { GuardedButton } from '@/components/common/bits';
import { DeviceMark } from '@/components/common/marks';
import { ProgressBar } from '@/components/common/ProgressBar';
import { Badge } from '@/components/ui/badge';
import { Surface } from '@/components/ui/section';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AskBox, useAsk } from '@/features/actions/AskBox';
import { VersionBadge } from '@/features/fleet/DeviceVersion';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import {
  heldByWorker,
  type Presence,
  presenceOf,
  progressFraction,
  runningByWorker,
} from '@/lib/fleet';
import { useFocusedRow } from '@/lib/focus';
import { headroomWindowLabel, minutesSince, percent, since } from '@/lib/format';
import { PRESENCE_LABEL } from '@/lib/presence';
import { cn } from '@/lib/utils';

export const PRESENCE_TONE: Record<Presence, string> = {
  busy: 'bg-info-bg text-info',
  idle: 'bg-ok-bg text-ok',
  paused: 'bg-warn-bg text-warn',
  offline: 'bg-raised text-fg-3',
};

/** The same four states as a raw colour, for the device mark's tinted well. */
export const PRESENCE_HUE: Record<Presence, string> = {
  busy: 'var(--dy-info)',
  idle: 'var(--dy-ok)',
  paused: 'var(--dy-warn)',
  offline: 'var(--dy-fg-3)',
};

/**
 * Window headroom.
 *
 * `unknown` means the device has never told us, and it must never render as a
 * bar: a bar at 100% for a device that has said nothing is the dashboard
 * confidently reporting a number it does not have, which is the exact failure
 * PRODUCT.md's third principle is about. It does not render as a sentence
 * either. "not reported yet" was printed once per row, and a fleet where no
 * device has reported is a column of the same five words; the fact belongs to
 * the device, so it moved into the presence chip's title, which is where the
 * rest of what this row knows about the machine already lives.
 *
 * The title also names which window (`window`) the percentage binds on
 * (issue #22), since "source: oauth" alone does not say whether the number is
 * the five-hour window or the weekly one.
 */
function HeadroomBar({
  headroom,
  source,
  window,
}: {
  headroom: number;
  source: string;
  window?: string | null;
}) {
  // Nothing at all, not a dash. This sits under the slots figure in one
  // Capacity cell now, and a bare "-" on its own line there reads as a missing
  // value rather than as "this device has never said". What it has never said
  // is in the presence chip's title, beside the rest of what the row knows
  // about the machine.
  if (source === 'unknown') return null;
  const clamped = Math.max(0, Math.min(1, headroom));
  const label = headroomWindowLabel(window);
  return (
    <span
      className="flex items-center gap-2"
      title={`${label ? `${label} window, ` : ''}source: ${source}`}
    >
      <span className="bg-raised h-1 w-16 overflow-hidden rounded-full">
        <span
          className={cn('block h-full rounded-full', clamped < 0.2 ? 'bg-warn' : 'bg-ok')}
          style={{ width: `${Math.round(clamped * 100)}%` }}
        />
      </span>
      <span className="tnum text-fg-2 text-2xs">{percent(clamped)}</span>
    </span>
  );
}

/**
 * What the presence chip means for this device, in a sentence, plus the headroom
 * when there is none to draw. Four presences and an optional window note is more
 * than a badge can say in one word, and this is the one place per row where the
 * word can be explained without spending a column on it.
 */
function presenceTitle(worker: WorkerRecord, presence: Presence): string {
  const state =
    presence === 'offline'
      ? 'No heartbeat for over two minutes, so the scheduler is passing it over.'
      : presence === 'paused'
        ? 'Online, but on do-not-disturb: it is not offered new work.'
        : presence === 'busy'
          ? 'Running work right now.'
          : 'Online and free.';
  const label = headroomWindowLabel(worker.headroomWindow);
  const window =
    worker.headroomSource === 'unknown'
      ? ' It has never reported how much of its Claude window is left.'
      : ` ${label ? `${label[0]!.toUpperCase()}${label.slice(1)} window` : 'Window'} headroom ${percent(worker.headroom)}, from ${worker.headroomSource}.`;
  return `${state}${window}${identitySuffix(worker)}`;
}

/**
 * Which accounts this device is actually signed in under, appended to the
 * presence tooltip rather than given its own column: this is read on the rare
 * row where it matters (a device flagged `policyViolation`, or a person
 * checking one by hand), never scanned across the whole fleet.
 */
function identitySuffix(worker: WorkerRecord): string {
  const parts: string[] = [];
  if (worker.agentAccount) {
    parts.push(
      `Claude: ${worker.agentAccount}${worker.agentOrganization ? ` (${worker.agentOrganization})` : ''}`,
    );
  }
  if (worker.ghLogin) parts.push(`GitHub: ${worker.ghLogin}`);
  return parts.length ? ` ${parts.join('. ')}.` : '';
}

/**
 * Whose machine this is, under its name. An admin's own laptop is in this
 * table as well as in the "Your agents" list above it, and the row that says
 * "you" is how the two are read as the same machine rather than as a
 * colleague who happens to share an address.
 */
function ownerLabel(worker: WorkerRecord, me: string | null | undefined): string {
  return me && worker.ownerEmail.toLowerCase() === me.toLowerCase() ? 'you' : worker.ownerEmail;
}

/**
 * The heartbeat column. "1h 11m" is a duration and reads as one: a person
 * scanning it has to supply "ago" themselves, and the ones who do not read it as
 * an interval. `since` states the unit.
 */
function heartbeatLabel(worker: WorkerRecord): string {
  if (!worker.lastHeartbeatAt) return 'never';
  return since(minutesSince(worker.lastHeartbeatAt));
}

/**
 * The projects a device is mapped to, but only when there are none.
 *
 * The chips were a column of their own, and in a fleet where every laptop is
 * mapped to the same two projects that column repeated the same two words down
 * every row for a quarter of the table's width. What a reader of this table
 * actually needs from the mapping is the exception: a device mapped to nothing
 * is offered nothing, which is otherwise indistinguishable from a device that is
 * merely quiet. The mapping itself belongs to the device, and its owner reads it
 * on their own card under "Your agents".
 */
function unmapped(worker: WorkerRecord) {
  if (worker.repos.length) return null;
  return <span className="text-fg-3 text-2xs">no projects mapped</span>;
}

/**
 * What an admin can do to one device.
 *
 * The pause toggle writes straight through, because it is reversible in one
 * click. Retiring asks first and the question is opened by the panel rather than
 * by this row: it has to render somewhere with room for a sentence and a
 * confirm, and this cell is 120px wide inside a horizontally scrolling
 * container, which is where an absolutely positioned box gets clipped into a
 * column of two-word lines.
 *
 * Pause is not offered to a device that is offline. It would write happily, but
 * it would be a control that changes nothing a person can observe: a machine
 * with no heartbeat is already not being offered work, and a fleet where the
 * only button on a dead row says "Pause" is a fleet that looks like it is
 * running.
 *
 * Both refusals go through `GuardedButton`, so the reason reaches a keyboard and
 * a screen reader as well as a hovering mouse. A `disabled` button leaves the
 * tab order, which puts the explanation somewhere a keyboard user cannot get
 * to, and it is the explanation that makes a refused control tolerable.
 */
function WorkerActions({
  worker,
  presence,
  held,
  onRetire,
}: {
  worker: WorkerRecord;
  presence: Presence;
  /** Unfinished tasks this device is holding, as the hub would name them. */
  held: string[] | undefined;
  onRetire: () => void;
}) {
  const toggle = useHubAction(() => api.setDnd(worker.id, !worker.dnd));
  const offline = presence === 'offline';
  const busy = held?.length
    ? `${worker.name} still holds ${held.join(', ')}. Cancel or finish those first.`
    : null;

  return (
    <div className="flex items-center justify-end gap-1 whitespace-nowrap">
      <GuardedButton
        variant="outline"
        size="xs"
        refusal={
          offline
            ? `${worker.name} has not sent a heartbeat for over two minutes. It is already being passed over; pausing it would change nothing until it checks in again.`
            : null
        }
        title={
          worker.dnd ? 'Let this device take work again' : 'Stop offering this device new work'
        }
        onClick={() => toggle.run()}
        disabled={toggle.busy}
      >
        {worker.dnd ? 'Resume' : 'Pause'}
      </GuardedButton>
      {/* The same rule the hub keeps: a device holding unfinished work cannot be
          retired, so the button says so rather than letting the write travel to
          a refusal. The hub answers it anyway -- this list can be a sweep
          behind -- and that answer lands in the question below the row. */}
      <GuardedButton
        variant="danger"
        size="xs"
        refusal={busy}
        title="Take this device out of the fleet and stop its token working"
        onClick={onRetire}
      >
        Retire
      </GuardedButton>
    </div>
  );
}

/**
 * What this device is doing, shared by the table cell and the phone card so the
 * two cannot drift into describing the same machine differently.
 */
function NowCell({ task }: { task: Task | undefined }) {
  // The word, not a dash. Every device on a quiet fleet drew "-" here and a
  // column of dashes says "no data" where the truth is "nothing running".
  if (!task) return <span className="text-fg-3 text-xs">nothing running</span>;
  const fraction = progressFraction(task);
  return (
    <div className="min-w-0">
      <Link
        to="/tasks/$taskId"
        params={{ taskId: task.id }}
        className="hover:text-gate block truncate text-xs transition-colors duration-100"
        title={task.title}
      >
        {task.title}
      </Link>
      <ProgressBar fraction={fraction} className="mt-1 w-36" />
      <span className="text-fg-3 mt-1 block text-2xs">
        {task.progress?.total
          ? `step ${Math.min(task.progress.done + 1, task.progress.total)} of ${task.progress.total}`
          : 'no todo list reported'}
      </span>
    </div>
  );
}

/**
 * One device on a phone.
 *
 * Seven columns do not become a scrollable table at 390px, they become a table
 * nobody scrolls: the answer is off screen and there is nothing on screen to
 * suggest it exists. So below `md` the row is a card, with the three facts a
 * person opens Fleet on a phone for -- which machine, whether it is alive, what
 * it is doing -- on the first lines, and the operational numbers on a quiet
 * second line under them.
 *
 * Headroom is dropped from the card rather than shown as a dash when the device
 * has never reported one. The table can put that in the presence chip's title;
 * a phone has no hover, so an unexplained "-" would be the whole story.
 */
function WorkerCard({
  worker,
  task,
  held,
  isAdmin,
  me,
  onRetire,
  confirming,
}: {
  worker: WorkerRecord;
  task: Task | undefined;
  held: string[] | undefined;
  isAdmin: boolean;
  /** The signed-in person's email, so their own row can say "you". */
  me: string | null | undefined;
  onRetire: () => void;
  /** The retire question, when it is this device's. Rendered under the card. */
  confirming: ReactNode;
}) {
  const presence = presenceOf(worker);
  return (
    <li className="px-gutter py-2.5">
      <div className="flex items-center gap-2.5">
        <DeviceMark hint={worker.hostname || worker.name} tone={PRESENCE_HUE[presence]} />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-xs font-medium">{worker.name}</span>
          <span className="text-fg-3 block truncate text-2xs">{ownerLabel(worker, me)}</span>
        </span>
        <Badge className={PRESENCE_TONE[presence]} title={presenceTitle(worker, presence)}>
          {PRESENCE_LABEL[presence]}
        </Badge>
        {worker.policyViolation ? (
          <Badge variant="destructive" title={worker.policyViolation}>
            Wrong account
          </Badge>
        ) : null}
        {worker.versionUpdate ? <VersionBadge update={worker.versionUpdate} /> : null}
      </div>
      {/* A bare "-" is a column convention, and there is no column here to make
          sense of it. The card says the thing instead. */}
      <div className="mt-1.5">
        {task ? <NowCell task={task} /> : <p className="text-fg-3 text-xs">Nothing running.</p>}
      </div>
      <div className="text-fg-3 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs">
        <span className="tnum">
          {worker.activeTaskCount}/{worker.maxConcurrency} slots
        </span>
        {worker.headroomSource === 'unknown' ? null : (
          <span className="tnum">
            {percent(worker.headroom)}
            {headroomWindowLabel(worker.headroomWindow)
              ? ` ${headroomWindowLabel(worker.headroomWindow)}`
              : ''}{' '}
            headroom
          </span>
        )}
        <span className="tnum">heartbeat {heartbeatLabel(worker)}</span>
      </div>
      {unmapped(worker) ? <div className="mt-1.5">{unmapped(worker)}</div> : null}
      {isAdmin ? (
        <div className="mt-2">
          <WorkerActions worker={worker} presence={presence} held={held} onRetire={onRetire} />
        </div>
      ) : null}
      {confirming ? <div className="-mx-gutter mt-2">{confirming}</div> : null}
    </li>
  );
}

export function WorkerTable({ workers, tasks }: { workers: WorkerRecord[]; tasks: Task[] }) {
  const { session } = useSession();
  const current = runningByWorker(tasks);
  const held = heldByWorker(tasks);
  /**
   * One retire question for the whole panel, keyed by device.
   *
   * It lives here rather than in the row because of where it has to be drawn: a
   * table row cannot hold a block, and the actions cell is too narrow and inside
   * a scrolling container. So the table renders it as a full-width row directly
   * under the device it is about, and the card list renders it under the card.
   * Either way it is one question at a time, about a device that is on screen.
   */
  const ask = useAsk();
  // The toast is raised here rather than through `success`, because it names the
  // device and the device is an argument.
  const retire = useHubAction(
    async (worker: WorkerRecord) => {
      await api.retireWorker(worker.id);
      toast.success(`${worker.name} retired. Its token no longer works.`);
    },
    { onDone: () => ask.close() },
  );

  /**
   * The panel owns one mutation, so its error belongs to whichever device asked
   * last. Without the reset, refusing to retire a busy device left that
   * sentence sitting under the next device's question, about a device it was
   * not about.
   */
  const openRetire = (workerId: string) => {
    retire.reset();
    ask.open('retire-worker', workerId);
  };

  const confirmFor = (worker: WorkerRecord) =>
    ask.isOpen('retire-worker', worker.id) && ask.ask ? (
      <AskBox
        ask={ask.ask}
        busy={retire.busy}
        error={retire.error}
        onCancel={ask.close}
        onConfirm={() => retire.run(worker)}
      />
    ) : null;
  // The device the palette pointed at, if this page was opened from there.
  const { focus, ref } = useFocusedRow<HTMLTableRowElement>();

  if (!workers.length) {
    return (
      <Surface>
        <p className="text-fg-3 px-gutter py-8 text-center text-sm">
          No workers enrolled. An admin mints an invite; the laptop redeems it once.
        </p>
      </Surface>
    );
  }

  return (
    /*
     * A `Surface`, not the retired `Panel`. `Panel` draws its own bordered
     * header bar, so this table arrived on the agents page under two headings
     * saying the same thing: "Everyone's devices, 3 devices" on the page
     * ground, and "Devices 3" in a bar immediately below it. The page names
     * the region; this draws the rows.
     */
    <Surface>
      <ul className="divide-y md:hidden">
        {workers.map((worker) => (
          <WorkerCard
            key={worker.id}
            worker={worker}
            task={current.get(worker.id)}
            held={held.get(worker.id)}
            isAdmin={session.isAdmin}
            me={session.user?.email}
            onRetire={() => openRetire(worker.id)}
            confirming={confirmFor(worker)}
          />
        ))}
      </ul>

      <Table className="max-md:hidden">
        <TableHeader>
          <TableRow>
            <TableHead>Worker</TableHead>
            <TableHead>Presence</TableHead>
            {/* Slots and window headroom were two columns at opposite ends of
                the row, and they are one question: has this machine got room
                for another task. Together they are also half the width. */}
            <TableHead>Capacity</TableHead>
            <TableHead className="w-[26%]">Now</TableHead>
            <TableHead className="text-right">Heartbeat</TableHead>
            {session.isAdmin ? <TableHead className="sr-only">Actions</TableHead> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {workers.map((worker) => {
            const presence = presenceOf(worker);
            const task = current.get(worker.id);
            const confirming = confirmFor(worker);
            const focused = worker.id === focus;
            return (
              <Fragment key={worker.id}>
                <TableRow ref={focused ? ref : undefined} className={cn(focused && 'bg-sel')}>
                  <TableCell>
                    <div className="flex min-w-0 items-center gap-2.5">
                      <DeviceMark
                        hint={worker.hostname || worker.name}
                        tone={PRESENCE_HUE[presence]}
                        className="size-7"
                      />
                      <div className="min-w-0">
                        <div className="truncate font-mono text-xs font-medium">{worker.name}</div>
                        <div className="text-fg-3 truncate text-2xs">
                          {ownerLabel(worker, session.user?.email)}
                        </div>
                        {unmapped(worker) ? <div className="mt-0.5">{unmapped(worker)}</div> : null}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge
                        className={PRESENCE_TONE[presence]}
                        title={presenceTitle(worker, presence)}
                      >
                        {PRESENCE_LABEL[presence]}
                      </Badge>
                      {worker.policyViolation ? (
                        <Badge variant="destructive" title={worker.policyViolation}>
                          Wrong account
                        </Badge>
                      ) : null}
                      {worker.versionUpdate ? <VersionBadge update={worker.versionUpdate} /> : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="tnum text-fg-2 text-xs whitespace-nowrap">
                      {worker.activeTaskCount}/{worker.maxConcurrency} slots
                    </div>
                    <div className="mt-1">
                      <HeadroomBar
                        headroom={worker.headroom}
                        source={worker.headroomSource}
                        window={worker.headroomWindow}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[20rem]">
                    <NowCell task={task} />
                  </TableCell>
                  <TableCell className="tnum text-fg-3 text-right text-2xs whitespace-nowrap">
                    {heartbeatLabel(worker)}
                  </TableCell>
                  {session.isAdmin ? (
                    <TableCell className="text-right">
                      <WorkerActions
                        worker={worker}
                        presence={presence}
                        held={held.get(worker.id)}
                        onRetire={() => openRetire(worker.id)}
                      />
                    </TableCell>
                  ) : null}
                </TableRow>
                {/* The question, full width, directly under the device it is
                    about. A row rather than a popover: this table scrolls
                    sideways, and anything absolutely positioned inside it is
                    clipped to a column and given its own scrollbars. */}
                {confirming ? (
                  <TableRow>
                    <TableCell className="p-0" colSpan={session.isAdmin ? 7 : 6}>
                      {confirming}
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Surface>
  );
}
